import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../../services/api';
import {
  startAdvancedSimulationRun,
  getAdvancedSimulationRun,
  generateClientRealismReview
} from '../../services/advancedSimulationApi';
import SandboxPersonaFilter from './SandboxPersonaFilter';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, FormControl, FormControlLabel, Grid,
  IconButton, InputLabel, LinearProgress, List, ListItem, ListItemText, MenuItem,
  Paper, Select, Slider, Switch, TextField, Typography, Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import ScienceIcon from '@mui/icons-material/Science';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';

// ── Suggested Values (not strict enums — LLM-generated personas may use novel values) ──

// Ensures a dropdown always includes the current value, even if it's not in the predefined list
const withCurrentValue = (options, currentValue) => {
  if (!currentValue || options.some(o => o.value === currentValue)) return options;
  const label = currentValue.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return [...options, { value: currentValue, label: `${label} (custom)` }];
};

const EDUCATION_LEVELS = [
  { value: 'none', label: 'None' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'trades', label: 'Trades' },
  { value: 'undergraduate', label: 'Undergraduate' },
  { value: 'postgraduate', label: 'Postgraduate' }
];

const COMMUNICATION_STYLES = [
  { value: 'terse', label: 'Terse' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'narrative', label: 'Narrative' },
  { value: 'analytical', label: 'Analytical' },
  { value: 'scattered', label: 'Scattered' },
  { value: 'tangential', label: 'Tangential' }
];

const MEMORY_CATEGORIES = [
  { value: 'childhood', label: 'Childhood' },
  { value: 'relationship', label: 'Relationship' },
  { value: 'trauma', label: 'Trauma' },
  { value: 'work', label: 'Work' },
  { value: 'family', label: 'Family' },
  { value: 'health', label: 'Health' },
  { value: 'achievement', label: 'Achievement' },
  { value: 'loss', label: 'Loss' }
];

const EMOTIONAL_CHARGES = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
  { value: 'overwhelming', label: 'Overwhelming' }
];

const ACCESSIBILITY_LEVELS = [
  { value: 'surface', label: 'Surface' },
  { value: 'mid_depth', label: 'Mid Depth' },
  { value: 'deep', label: 'Deep' }
];

const AWARENESS_LEVELS = [
  { value: 'fully_aware', label: 'Fully Aware' },
  { value: 'partially_aware', label: 'Partially Aware' },
  { value: 'unaware', label: 'Unaware' }
];

const OPENING_STYLES = [
  { value: 'reluctant', label: 'Reluctant' },
  { value: 'guarded', label: 'Guarded' },
  { value: 'compliant', label: 'Compliant' },
  { value: 'eager', label: 'Eager' },
  { value: 'hostile', label: 'Hostile' }
];

const ALLIANCE_BUILDING_RATES = [
  { value: 'very_slow', label: 'Very Slow' },
  { value: 'slow', label: 'Slow' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'fast', label: 'Fast' }
];

const RESISTANCE_LEVELS = [
  { value: 'none', label: 'None' },
  { value: 'mild', label: 'Mild' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' }
];

const ESCALATION_BEHAVIORS = [
  { value: 'static', label: 'Static' },
  { value: 'escalating', label: 'Escalating' },
  { value: 'intermittent', label: 'Intermittent' },
  { value: 'decreasing', label: 'Decreasing' }
];

const RESISTANCE_PATTERN_OPTIONS = [
  { value: 'therapy_rejection', label: 'Therapy Rejection' },
  { value: 'normalcy_demand', label: 'Normalcy Demand' },
  { value: 'role_challenge', label: 'Role Challenge' },
  { value: 'avoidance_preference', label: 'Avoidance Preference' },
  { value: 'therapist_criticism', label: 'Therapist Criticism' },
  { value: 'session_control', label: 'Session Control' },
  { value: 'emotional_deflection', label: 'Emotional Deflection' },
  { value: 'boundary_testing', label: 'Boundary Testing' },
  { value: 'topic_fixation', label: 'Topic Fixation' },
  { value: 'help_rejection', label: 'Help Rejection' }
];

// ── Default Form Data ───────────────────────────────────────────────────────

const DEFAULT_FORM_DATA = {
  key: '',
  name: '',
  opening_line: '',
  system_prompt: '',
  description: '',
  metadata: {
    conditions: [],
    severity: '',
    demographics: { ageRange: '', gender: '', occupation: '' }
  },
  cognitiveProfile: {
    vocabularyLevel: 5,
    emotionalArticulation: 5,
    introspectiveCapacity: 5,
    educationLevel: 'secondary',
    communicationStyle: 'concrete'
  },
  presentingConcerns: [],
  memoryBank: [],
  sessionDynamics: {
    openingStyle: 'guarded',
    allianceBuildingRate: 'slow',
    deflectionPatterns: [],
    silenceComfort: 3
  },
  resistanceBehavior: {
    enabled: false,
    resistanceLevel: 'none',
    resistancePatterns: [],
    resistanceTriggers: [],
    escalationBehavior: 'static',
    resistanceOnset: 0,
    resistanceProbability: 0.7
  }
};

// ── Helper: deep merge loaded persona with defaults ─────────────────────────

const mergeWithDefaults = (persona) => {
  if (!persona) return { ...DEFAULT_FORM_DATA };
  return {
    ...DEFAULT_FORM_DATA,
    ...persona,
    metadata: {
      ...DEFAULT_FORM_DATA.metadata,
      ...(persona.metadata || {}),
      demographics: {
        ...DEFAULT_FORM_DATA.metadata.demographics,
        ...(persona.metadata?.demographics || {})
      }
    },
    cognitiveProfile: {
      ...DEFAULT_FORM_DATA.cognitiveProfile,
      ...(persona.cognitiveProfile || {})
    },
    sessionDynamics: {
      ...DEFAULT_FORM_DATA.sessionDynamics,
      ...(persona.sessionDynamics || {})
    },
    resistanceBehavior: {
      ...DEFAULT_FORM_DATA.resistanceBehavior,
      ...(persona.resistanceBehavior || {})
    },
    presentingConcerns: persona.presentingConcerns || [],
    memoryBank: persona.memoryBank || []
  };
};

// ── Component ───────────────────────────────────────────────────────────────

const ClientPersonaEditor = ({ token }) => {
  const [personas, setPersonas] = useState([]);
  const [selectedPersonaKey, setSelectedPersonaKey] = useState('');
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedAccordion, setExpandedAccordion] = useState('core-identity');

  // Chip input state for tags/patterns
  const [conditionInput, setConditionInput] = useState('');
  const [deflectionInput, setDeflectionInput] = useState('');
  const [triggerTagInputs, setTriggerTagInputs] = useState({});

  // Quick Test state
  const [therapistPersonas, setTherapistPersonas] = useState([]);
  const [selectedTherapistKey, setSelectedTherapistKey] = useState('');
  const [quickTestPhase, setQuickTestPhase] = useState('idle'); // idle|simulating|reviewing|complete|error
  const [quickTestResults, setQuickTestResults] = useState(null);
  const [quickTestError, setQuickTestError] = useState('');
  const [quickTestProgress, setQuickTestProgress] = useState('');
  const pollRef = useRef(null);

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // { personas, newKeys, updateKeys, enrichedCount, basicCount }
  const importFileRef = useRef(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchPersonas = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await apiService.getAdminClientPersonas(token);
      setPersonas(data || []);
    } catch (err) {
      setError('Failed to load client personas.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  // Fetch therapist personas for Quick Test
  useEffect(() => {
    if (!token) return;
    apiService.getAdminTherapistPersonas(token)
      .then(data => setTherapistPersonas(data || []))
      .catch(err => console.error('Failed to load therapist personas for quick test:', err));
  }, [token]);

  // ── State helpers ─────────────────────────────────────────────────────────

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (arrayName, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], defaultItem]
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const updateResistanceTrigger = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      resistanceBehavior: {
        ...prev.resistanceBehavior,
        resistanceTriggers: prev.resistanceBehavior.resistanceTriggers.map((t, i) =>
          i === index ? { ...t, [field]: value } : t
        )
      }
    }));
  };

  const addResistanceTrigger = () => {
    setFormData(prev => ({
      ...prev,
      resistanceBehavior: {
        ...prev.resistanceBehavior,
        resistanceTriggers: [
          ...prev.resistanceBehavior.resistanceTriggers,
          { trigger: '', response: '', intensity: 5 }
        ]
      }
    }));
  };

  const removeResistanceTrigger = (index) => {
    setFormData(prev => ({
      ...prev,
      resistanceBehavior: {
        ...prev.resistanceBehavior,
        resistanceTriggers: prev.resistanceBehavior.resistanceTriggers.filter((_, i) => i !== index)
      }
    }));
  };

  // ── Persona selection / new / clone ───────────────────────────────────────

  const handleSelectPersona = (key) => {
    setSelectedPersonaKey(key);
    const selected = personas.find(p => p.key === key);
    if (selected) {
      setFormData(mergeWithDefaults(selected));
      setIsEditing(true);
    } else {
      handleNewPersona();
    }
    setError('');
    setSuccess('');
  };

  const handleNewPersona = () => {
    setSelectedPersonaKey('');
    setFormData({ ...DEFAULT_FORM_DATA, metadata: { ...DEFAULT_FORM_DATA.metadata, demographics: { ...DEFAULT_FORM_DATA.metadata.demographics } }, cognitiveProfile: { ...DEFAULT_FORM_DATA.cognitiveProfile }, sessionDynamics: { ...DEFAULT_FORM_DATA.sessionDynamics, deflectionPatterns: [] }, resistanceBehavior: { ...DEFAULT_FORM_DATA.resistanceBehavior, resistancePatterns: [], resistanceTriggers: [] }, presentingConcerns: [], memoryBank: [] });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleClonePersona = () => {
    if (!selectedPersonaKey) return;
    setFormData(prev => ({
      ...prev,
      key: prev.key + '_clone',
      name: prev.name + ' (Clone)'
    }));
    setSelectedPersonaKey('');
    setIsEditing(false);
    setSuccess('Change the key and description, then click Save to create the clone.');
  };

  const handleCloneFromSandbox = async (sandboxPersona) => {
    const payload = mergeWithDefaults(sandboxPersona);
    delete payload.key; // will be set from sandboxPersona.key below
    const response = await apiService.addClientPersona({
      ...payload,
      key: sandboxPersona.key
    }, token);
    setPersonas(prev => [...prev, response.persona]);
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    if (!formData.key || !formData.name || !formData.opening_line || !formData.system_prompt) {
      setError('Key, Name, Opening Line, and System Prompt are required.');
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        name: formData.name,
        opening_line: formData.opening_line,
        system_prompt: formData.system_prompt,
        description: formData.description,
        metadata: formData.metadata
      };

      // Include 4-layer fields only if they have meaningful content
      if (formData.cognitiveProfile) payload.cognitiveProfile = formData.cognitiveProfile;
      if (formData.presentingConcerns?.length) payload.presentingConcerns = formData.presentingConcerns;
      if (formData.memoryBank?.length) payload.memoryBank = formData.memoryBank;
      if (formData.sessionDynamics) payload.sessionDynamics = formData.sessionDynamics;
      if (formData.resistanceBehavior) payload.resistanceBehavior = formData.resistanceBehavior;

      let response;
      if (isEditing) {
        response = await apiService.updateClientPersona(selectedPersonaKey, payload, token);
        setSuccess(response.msg || 'Persona updated successfully!');
        setPersonas(prev => prev.map(p => p.key === selectedPersonaKey ? response.persona : p));
      } else {
        payload.key = formData.key;
        response = await apiService.addClientPersona(payload, token);
        setSuccess(response.msg || 'Persona added successfully!');
        setPersonas(prev => [...prev, response.persona]);
        handleSelectPersona(response.persona.key);
      }
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to save persona.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPersonaKey || !window.confirm(`Are you sure you want to delete persona "${formData.name}"?`)) return;
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiService.deleteClientPersona(selectedPersonaKey, token);
      setSuccess('Persona deleted successfully!');
      setPersonas(prev => prev.filter(p => p.key !== selectedPersonaKey));
      handleNewPersona();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to delete persona.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Quick Test ───────────────────────────────────────────────────────────

  const handleQuickTest = async () => {
    if (!selectedPersonaKey || !selectedTherapistKey) return;
    setQuickTestPhase('simulating');
    setQuickTestResults(null);
    setQuickTestError('');
    setQuickTestProgress('Starting simulation...');

    try {
      // 1. Start a simulation run
      const runResponse = await startAdvancedSimulationRun({
        clientPersonaKey: selectedPersonaKey,
        therapistPersonaKey: selectedTherapistKey,
        maxTurns: 20,
        tags: ['quick-test']
      }, token);

      const runId = runResponse._id || runResponse.run?._id;
      if (!runId) throw new Error('No run ID returned');

      // 2. Poll until completed
      setQuickTestProgress('Simulation running...');
      await new Promise((resolve, reject) => {
        let attempts = 0;
        pollRef.current = setInterval(async () => {
          attempts++;
          try {
            const status = await getAdvancedSimulationRun(runId, token);
            const elapsed = Math.round(attempts * 2);
            setQuickTestProgress(`Simulation running... (${elapsed}s elapsed)`);

            const runStatus = status.status;
            if (runStatus === 'completed') {
              clearInterval(pollRef.current);
              pollRef.current = null;
              resolve();
            } else if (runStatus === 'failed' || runStatus === 'error') {
              clearInterval(pollRef.current);
              pollRef.current = null;
              reject(new Error(`Simulation ${runStatus}`));
            } else if (attempts > 150) { // ~5 min timeout
              clearInterval(pollRef.current);
              pollRef.current = null;
              reject(new Error('Simulation timed out'));
            }
          } catch (err) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            reject(err);
          }
        }, 2000);
      });

      // 3. Generate client realism review
      setQuickTestPhase('reviewing');
      setQuickTestProgress('Generating realism review...');
      const reviewResponse = await generateClientRealismReview(runId, token);
      setQuickTestResults(reviewResponse.review);
      setQuickTestPhase('complete');
    } catch (err) {
      console.error('Quick test failed:', err);
      setQuickTestError(err.message || 'Quick test failed');
      setQuickTestPhase('error');
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // ── Accordion handler ─────────────────────────────────────────────────────

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  // ── Chip helpers ──────────────────────────────────────────────────────────

  const handleAddCondition = () => {
    const val = conditionInput.trim();
    if (!val) return;
    if (formData.metadata.conditions.includes(val)) return;
    setFormData(prev => ({
      ...prev,
      metadata: { ...prev.metadata, conditions: [...prev.metadata.conditions, val] }
    }));
    setConditionInput('');
  };

  const handleRemoveCondition = (idx) => {
    setFormData(prev => ({
      ...prev,
      metadata: { ...prev.metadata, conditions: prev.metadata.conditions.filter((_, i) => i !== idx) }
    }));
  };

  const handleAddDeflection = () => {
    const val = deflectionInput.trim();
    if (!val) return;
    if (formData.sessionDynamics.deflectionPatterns.includes(val)) return;
    updateNestedField('sessionDynamics', 'deflectionPatterns', [...formData.sessionDynamics.deflectionPatterns, val]);
    setDeflectionInput('');
  };

  const handleRemoveDeflection = (idx) => {
    updateNestedField('sessionDynamics', 'deflectionPatterns', formData.sessionDynamics.deflectionPatterns.filter((_, i) => i !== idx));
  };

  const handleAddTriggerTag = (memIdx) => {
    const val = (triggerTagInputs[memIdx] || '').trim();
    if (!val) return;
    const mem = formData.memoryBank[memIdx];
    if (mem.triggerTags?.includes(val)) return;
    updateArrayItem('memoryBank', memIdx, 'triggerTags', [...(mem.triggerTags || []), val]);
    setTriggerTagInputs(prev => ({ ...prev, [memIdx]: '' }));
  };

  const handleRemoveTriggerTag = (memIdx, tagIdx) => {
    const mem = formData.memoryBank[memIdx];
    updateArrayItem('memoryBank', memIdx, 'triggerTags', mem.triggerTags.filter((_, i) => i !== tagIdx));
  };

  // ── Export / Import ──────────────────────────────────────────────────────

  const stripDbFields = (persona) => {
    const { _id, __v, createdAt, updatedAt, isActive, ...exportData } = persona;
    return exportData;
  };

  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportSelected = () => {
    if (!selectedPersonaKey) return;
    const exportData = stripDbFields(formData);
    downloadJson(exportData, `${formData.key}-persona.json`);
  };

  const handleExportAll = () => {
    if (!personas.length) return;
    const exportData = personas.map(stripDbFields);
    const date = new Date().toISOString().slice(0, 10);
    downloadJson(exportData, `client-personas-export-${date}.json`);
  };

  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const parsed = JSON.parse(evt.target.result);
        const personaList = Array.isArray(parsed) ? parsed : [parsed];

        // Validate required fields
        const invalid = personaList.filter(p => !p.key || !p.name || !p.opening_line || !p.system_prompt);
        if (invalid.length) {
          setError(`Import failed: ${invalid.length} persona(s) missing required fields (key, name, opening_line, system_prompt).`);
          return;
        }

        const existingKeys = new Set(personas.map(p => p.key));
        const newKeys = [];
        const updateKeys = [];
        let enrichedCount = 0;
        let basicCount = 0;

        for (const p of personaList) {
          if (existingKeys.has(p.key)) {
            updateKeys.push(p.key);
          } else {
            newKeys.push(p.key);
          }
          const isEnriched = p.cognitiveProfile || p.memoryBank || p.sessionDynamics;
          if (isEnriched) enrichedCount++;
          else basicCount++;
        }

        setImportPreview({ personas: personaList, newKeys, updateKeys, enrichedCount, basicCount });
        setImportDialogOpen(true);
      } catch (err) {
        setError('Import failed: Invalid JSON file.');
      }
    };
    reader.readAsText(file);
  };

  const handleImportConfirm = async () => {
    if (!importPreview) return;
    setImportDialogOpen(false);
    setIsLoading(true);
    setError('');
    setSuccess('');

    let created = 0;
    let updated = 0;
    const errors = [];
    const existingKeys = new Set(personas.map(p => p.key));

    for (const raw of importPreview.personas) {
      const persona = stripDbFields(raw);
      try {
        if (existingKeys.has(persona.key)) {
          const { key, ...payload } = persona;
          await apiService.updateClientPersona(persona.key, payload, token);
          updated++;
        } else {
          await apiService.addClientPersona(persona, token);
          created++;
        }
      } catch (err) {
        errors.push(`${persona.key}: ${err.response?.data?.msg || err.message}`);
      }
    }

    await fetchPersonas();
    setIsLoading(false);
    setImportPreview(null);

    if (errors.length) {
      setError(`Import completed with errors: ${errors.join('; ')}`);
    } else {
      setSuccess(`Import complete: ${created} created, ${updated} updated.`);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Client Persona Editor</Typography>

      {/* Sandbox Persona Browser */}
      <SandboxPersonaFilter token={token} personaType="client" onCloneSandbox={handleCloneFromSandbox} />

      {/* Persona Selection */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Select Persona</InputLabel>
          <Select
            value={selectedPersonaKey}
            label="Select Persona"
            onChange={(e) => handleSelectPersona(e.target.value)}
            disabled={isLoading}
          >
            <MenuItem value="">-- Select Existing --</MenuItem>
            {personas.map(p => (
              <MenuItem key={p.key} value={p.key}>{p.name} ({p.key})</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={handleNewPersona} disabled={isLoading} startIcon={<AddIcon />}>
          Create New
        </Button>
        {isEditing && (
          <Button variant="outlined" color="info" onClick={handleClonePersona} disabled={isLoading} startIcon={<ContentCopyIcon />}>
            Clone
          </Button>
        )}
        {isEditing && (
          <Button variant="outlined" color="error" onClick={handleDelete} disabled={isLoading} startIcon={<DeleteIcon />}>
            Delete
          </Button>
        )}
        <Divider orientation="vertical" flexItem />
        {isEditing && (
          <Button variant="outlined" onClick={handleExportSelected} disabled={isLoading} startIcon={<FileDownloadIcon />}>
            Export Selected
          </Button>
        )}
        <Button variant="outlined" onClick={handleExportAll} disabled={isLoading || !personas.length} startIcon={<FileDownloadIcon />}>
          Export All
        </Button>
        <Button variant="outlined" onClick={() => importFileRef.current?.click()} disabled={isLoading} startIcon={<FileUploadIcon />}>
          Import
        </Button>
        <input
          ref={importFileRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleImportFileSelect}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>}

      {/* ─── Form ────────────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit}>

        {/* ── Section 1: Core Identity ─────────────────────────────────── */}
        <Accordion expanded={expandedAccordion === 'core-identity'} onChange={handleAccordionChange('core-identity')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Core Identity</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth size="small" label="Key" required
                  value={formData.key} onChange={(e) => updateField('key', e.target.value)}
                  disabled={isLoading || isEditing}
                  placeholder="e.g., client_anxiety_mild"
                  helperText={isEditing ? 'Cannot change key when editing' : ''}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth size="small" label="Name" required
                  value={formData.name} onChange={(e) => updateField('name', e.target.value)}
                  disabled={isLoading}
                  placeholder="e.g., Mild Social Anxiety Client"
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth size="small" label="Opening Line" required multiline rows={3}
                  value={formData.opening_line} onChange={(e) => updateField('opening_line', e.target.value)}
                  disabled={isLoading}
                  placeholder="The first thing the client says in the simulation."
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth size="small" label="System Prompt" required multiline rows={8}
                  value={formData.system_prompt} onChange={(e) => updateField('system_prompt', e.target.value)}
                  disabled={isLoading}
                  placeholder="Detailed instructions for the AI on how to act as this client."
                  slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.85rem' } } }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth size="small" label="Description" multiline rows={4}
                  value={formData.description || ''} onChange={(e) => updateField('description', e.target.value)}
                  disabled={isLoading}
                  placeholder="Background, presenting problems, session behavior, etc."
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Section 2: Metadata ──────────────────────────────────────── */}
        <Accordion expanded={expandedAccordion === 'metadata'} onChange={handleAccordionChange('metadata')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Metadata</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {/* Conditions as chips */}
              <Grid size={12}>
                <Typography variant="body2" sx={{ mb: 1 }}>Conditions</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {formData.metadata.conditions.map((c, i) => (
                    <Chip key={i} label={c} onDelete={() => handleRemoveCondition(i)} size="small" />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small" placeholder="Add condition..." value={conditionInput}
                    onChange={(e) => setConditionInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCondition(); } }}
                    disabled={isLoading}
                  />
                  <Button size="small" variant="outlined" onClick={handleAddCondition} disabled={isLoading}>Add</Button>
                </Box>
              </Grid>

              {/* Severity */}
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={formData.metadata.severity || ''} label="Severity"
                    onChange={(e) => setFormData(prev => ({ ...prev, metadata: { ...prev.metadata, severity: e.target.value } }))}
                    disabled={isLoading}
                  >
                    <MenuItem value="">-- Select --</MenuItem>
                    <MenuItem value="mild">Mild</MenuItem>
                    <MenuItem value="moderate">Moderate</MenuItem>
                    <MenuItem value="severe">Severe</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Demographics */}
              <Grid size={12}><Divider sx={{ my: 1 }}><Typography variant="caption">Demographics</Typography></Divider></Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth size="small" label="Age Range" placeholder="e.g., 25-30"
                  value={formData.metadata.demographics?.ageRange || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev, metadata: { ...prev.metadata, demographics: { ...prev.metadata.demographics, ageRange: e.target.value } }
                  }))}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth size="small" label="Gender" placeholder="e.g., female"
                  value={formData.metadata.demographics?.gender || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev, metadata: { ...prev.metadata, demographics: { ...prev.metadata.demographics, gender: e.target.value } }
                  }))}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth size="small" label="Occupation" placeholder="e.g., graphic_designer"
                  value={formData.metadata.demographics?.occupation || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev, metadata: { ...prev.metadata, demographics: { ...prev.metadata.demographics, occupation: e.target.value } }
                  }))}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Section 3: Cognitive Profile ──────────────────────────────── */}
        <Accordion expanded={expandedAccordion === 'cognitive-profile'} onChange={handleAccordionChange('cognitive-profile')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Cognitive Profile</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" gutterBottom>Vocabulary Level: {formData.cognitiveProfile.vocabularyLevel}</Typography>
                <Slider
                  value={formData.cognitiveProfile.vocabularyLevel} min={1} max={10} step={1}
                  marks valueLabelDisplay="auto"
                  onChange={(_, val) => updateNestedField('cognitiveProfile', 'vocabularyLevel', val)}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" gutterBottom>Emotional Articulation: {formData.cognitiveProfile.emotionalArticulation}</Typography>
                <Slider
                  value={formData.cognitiveProfile.emotionalArticulation} min={1} max={10} step={1}
                  marks valueLabelDisplay="auto"
                  onChange={(_, val) => updateNestedField('cognitiveProfile', 'emotionalArticulation', val)}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" gutterBottom>Introspective Capacity: {formData.cognitiveProfile.introspectiveCapacity}</Typography>
                <Slider
                  value={formData.cognitiveProfile.introspectiveCapacity} min={1} max={10} step={1}
                  marks valueLabelDisplay="auto"
                  onChange={(_, val) => updateNestedField('cognitiveProfile', 'introspectiveCapacity', val)}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Education Level</InputLabel>
                  <Select
                    value={formData.cognitiveProfile.educationLevel} label="Education Level"
                    onChange={(e) => updateNestedField('cognitiveProfile', 'educationLevel', e.target.value)}
                    disabled={isLoading}
                  >
                    {withCurrentValue(EDUCATION_LEVELS, formData.cognitiveProfile.educationLevel).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Communication Style</InputLabel>
                  <Select
                    value={formData.cognitiveProfile.communicationStyle} label="Communication Style"
                    onChange={(e) => updateNestedField('cognitiveProfile', 'communicationStyle', e.target.value)}
                    disabled={isLoading}
                  >
                    {withCurrentValue(COMMUNICATION_STYLES, formData.cognitiveProfile.communicationStyle).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Section 4: Presenting Concerns ───────────────────────────── */}
        <Accordion expanded={expandedAccordion === 'presenting-concerns'} onChange={handleAccordionChange('presenting-concerns')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              Presenting Concerns {formData.presentingConcerns.length > 0 && `(${formData.presentingConcerns.length})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {formData.presentingConcerns.map((pc, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">Concern #{idx + 1}</Typography>
                  <IconButton size="small" color="error" onClick={() => removeArrayItem('presentingConcerns', idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField
                      fullWidth size="small" label="Concern" multiline rows={2}
                      value={pc.concern || ''} onChange={(e) => updateArrayItem('presentingConcerns', idx, 'concern', e.target.value)}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Awareness Level</InputLabel>
                      <Select
                        value={pc.awarenessLevel || 'fully_aware'} label="Awareness Level"
                        onChange={(e) => updateArrayItem('presentingConcerns', idx, 'awarenessLevel', e.target.value)}
                        disabled={isLoading}
                      >
                        {withCurrentValue(AWARENESS_LEVELS, pc.awarenessLevel).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  {pc.awarenessLevel === 'partially_aware' && (
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <TextField
                        fullWidth size="small" label="Surface Presentation"
                        value={pc.surfacePresentation || ''} onChange={(e) => updateArrayItem('presentingConcerns', idx, 'surfacePresentation', e.target.value)}
                        disabled={isLoading}
                        placeholder="How the client presents this concern at the surface level"
                      />
                    </Grid>
                  )}
                  <Grid size={12}>
                    <TextField
                      fullWidth size="small" label="Underlying Dynamic"
                      value={pc.underlyingDynamic || ''} onChange={(e) => updateArrayItem('presentingConcerns', idx, 'underlyingDynamic', e.target.value)}
                      disabled={isLoading}
                      placeholder="The deeper dynamic behind this concern"
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              startIcon={<AddIcon />} variant="outlined" size="small"
              onClick={() => addArrayItem('presentingConcerns', { concern: '', awarenessLevel: 'fully_aware', surfacePresentation: '', underlyingDynamic: '' })}
              disabled={isLoading}
            >
              Add Concern
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* ── Section 5: Memory Bank ───────────────────────────────────── */}
        <Accordion expanded={expandedAccordion === 'memory-bank'} onChange={handleAccordionChange('memory-bank')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              Memory Bank {formData.memoryBank.length > 0 && `(${formData.memoryBank.length})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {formData.memoryBank.map((mem, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">Memory #{idx + 1}</Typography>
                  <IconButton size="small" color="error" onClick={() => removeArrayItem('memoryBank', idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Grid container spacing={2}>
                  {/* Row 1: ID, Category, Emotional Charge, Accessibility */}
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      fullWidth size="small" label="ID" value={mem.id || ''} disabled
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={mem.category || ''} label="Category"
                        onChange={(e) => updateArrayItem('memoryBank', idx, 'category', e.target.value)}
                        disabled={isLoading}
                      >
                        {withCurrentValue(MEMORY_CATEGORIES, mem.category).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Emotional Charge</InputLabel>
                      <Select
                        value={mem.emotionalCharge || 'moderate'} label="Emotional Charge"
                        onChange={(e) => updateArrayItem('memoryBank', idx, 'emotionalCharge', e.target.value)}
                        disabled={isLoading}
                      >
                        {withCurrentValue(EMOTIONAL_CHARGES, mem.emotionalCharge).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Accessibility</InputLabel>
                      <Select
                        value={mem.accessibility || 'surface'} label="Accessibility"
                        onChange={(e) => updateArrayItem('memoryBank', idx, 'accessibility', e.target.value)}
                        disabled={isLoading}
                      >
                        {withCurrentValue(ACCESSIBILITY_LEVELS, mem.accessibility).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Row 2: Content */}
                  <Grid size={12}>
                    <TextField
                      fullWidth size="small" label="Content" multiline rows={3}
                      value={mem.content || ''} onChange={(e) => updateArrayItem('memoryBank', idx, 'content', e.target.value)}
                      disabled={isLoading}
                      placeholder="The memory content..."
                    />
                  </Grid>

                  {/* Row 3: Trigger Tags */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Trigger Tags</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {(mem.triggerTags || []).map((tag, ti) => (
                        <Chip key={ti} label={tag} onDelete={() => handleRemoveTriggerTag(idx, ti)} size="small" />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small" placeholder="Add tag..." value={triggerTagInputs[idx] || ''}
                        onChange={(e) => setTriggerTagInputs(prev => ({ ...prev, [idx]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTriggerTag(idx); } }}
                        disabled={isLoading}
                      />
                      <Button size="small" variant="outlined" onClick={() => handleAddTriggerTag(idx)} disabled={isLoading}>Add</Button>
                    </Box>
                  </Grid>

                  {/* Row 4: Physical Response + Partial Recall */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth size="small" label="Physical Response"
                      value={mem.physicalResponse || ''} onChange={(e) => updateArrayItem('memoryBank', idx, 'physicalResponse', e.target.value)}
                      disabled={isLoading}
                      placeholder="e.g., tension in shoulders"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth size="small" label="Partial Recall"
                      value={mem.partialRecall || ''} onChange={(e) => updateArrayItem('memoryBank', idx, 'partialRecall', e.target.value)}
                      disabled={isLoading}
                      placeholder="Fragmented version of this memory"
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              startIcon={<AddIcon />} variant="outlined" size="small"
              onClick={() => addArrayItem('memoryBank', {
                id: `mem_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                category: 'childhood', content: '', emotionalCharge: 'moderate',
                accessibility: 'surface', triggerTags: [], physicalResponse: '', partialRecall: ''
              })}
              disabled={isLoading}
            >
              Add Memory
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* ── Section 6: Session Dynamics ───────────────────────────────── */}
        <Accordion expanded={expandedAccordion === 'session-dynamics'} onChange={handleAccordionChange('session-dynamics')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Session Dynamics</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Opening Style</InputLabel>
                  <Select
                    value={formData.sessionDynamics.openingStyle} label="Opening Style"
                    onChange={(e) => updateNestedField('sessionDynamics', 'openingStyle', e.target.value)}
                    disabled={isLoading}
                  >
                    {withCurrentValue(OPENING_STYLES, formData.sessionDynamics.openingStyle).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Alliance Building Rate</InputLabel>
                  <Select
                    value={formData.sessionDynamics.allianceBuildingRate} label="Alliance Building Rate"
                    onChange={(e) => updateNestedField('sessionDynamics', 'allianceBuildingRate', e.target.value)}
                    disabled={isLoading}
                  >
                    {withCurrentValue(ALLIANCE_BUILDING_RATES, formData.sessionDynamics.allianceBuildingRate).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" gutterBottom>Silence Comfort: {formData.sessionDynamics.silenceComfort}</Typography>
                <Slider
                  value={formData.sessionDynamics.silenceComfort} min={1} max={10} step={1}
                  marks valueLabelDisplay="auto"
                  onChange={(_, val) => updateNestedField('sessionDynamics', 'silenceComfort', val)}
                  disabled={isLoading}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" sx={{ mb: 1 }}>Deflection Patterns</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {formData.sessionDynamics.deflectionPatterns.map((d, i) => (
                    <Chip key={i} label={d} onDelete={() => handleRemoveDeflection(i)} size="small" />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small" placeholder="Add deflection pattern..." value={deflectionInput}
                    onChange={(e) => setDeflectionInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDeflection(); } }}
                    disabled={isLoading}
                  />
                  <Button size="small" variant="outlined" onClick={handleAddDeflection} disabled={isLoading}>Add</Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Section 7: Resistance Behavior ───────────────────────────── */}
        <Accordion expanded={expandedAccordion === 'resistance-behavior'} onChange={handleAccordionChange('resistance-behavior')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Resistance Behavior</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.resistanceBehavior.enabled}
                      onChange={(e) => updateNestedField('resistanceBehavior', 'enabled', e.target.checked)}
                      disabled={isLoading}
                    />
                  }
                  label="Enable Resistance Behavior"
                />
              </Grid>

              {formData.resistanceBehavior.enabled && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Resistance Level</InputLabel>
                      <Select
                        value={formData.resistanceBehavior.resistanceLevel} label="Resistance Level"
                        onChange={(e) => updateNestedField('resistanceBehavior', 'resistanceLevel', e.target.value)}
                        disabled={isLoading}
                      >
                        {withCurrentValue(RESISTANCE_LEVELS, formData.resistanceBehavior.resistanceLevel).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Escalation Behavior</InputLabel>
                      <Select
                        value={formData.resistanceBehavior.escalationBehavior} label="Escalation Behavior"
                        onChange={(e) => updateNestedField('resistanceBehavior', 'escalationBehavior', e.target.value)}
                        disabled={isLoading}
                      >
                        {withCurrentValue(ESCALATION_BEHAVIORS, formData.resistanceBehavior.escalationBehavior).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Resistance Patterns as chips */}
                  <Grid size={12}>
                    <Typography variant="body2" sx={{ mb: 1 }}>Resistance Patterns</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {(formData.resistanceBehavior.resistancePatterns || []).map((p, i) => {
                        const label = RESISTANCE_PATTERN_OPTIONS.find(o => o.value === p)?.label || p;
                        return (
                          <Chip
                            key={i} label={label} size="small"
                            onDelete={() => updateNestedField('resistanceBehavior', 'resistancePatterns',
                              formData.resistanceBehavior.resistancePatterns.filter((_, idx) => idx !== i)
                            )}
                          />
                        );
                      })}
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Add Pattern</InputLabel>
                      <Select
                        value="" label="Add Pattern"
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!formData.resistanceBehavior.resistancePatterns.includes(val)) {
                            updateNestedField('resistanceBehavior', 'resistancePatterns',
                              [...formData.resistanceBehavior.resistancePatterns, val]
                            );
                          }
                        }}
                        disabled={isLoading}
                      >
                        {RESISTANCE_PATTERN_OPTIONS
                          .filter(o => !formData.resistanceBehavior.resistancePatterns.includes(o.value))
                          .map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)
                        }
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Onset turn + Probability */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth size="small" label="Onset Turn Number" type="number"
                      value={formData.resistanceBehavior.resistanceOnset}
                      onChange={(e) => updateNestedField('resistanceBehavior', 'resistanceOnset', parseInt(e.target.value) || 0)}
                      disabled={isLoading}
                      slotProps={{ input: { inputProps: { min: 0 } } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" gutterBottom>
                      Resistance Probability: {formData.resistanceBehavior.resistanceProbability}
                    </Typography>
                    <Slider
                      value={formData.resistanceBehavior.resistanceProbability}
                      min={0} max={1} step={0.05} valueLabelDisplay="auto"
                      onChange={(_, val) => updateNestedField('resistanceBehavior', 'resistanceProbability', val)}
                      disabled={isLoading}
                    />
                  </Grid>

                  {/* Resistance Triggers */}
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }}><Typography variant="caption">Resistance Triggers</Typography></Divider>
                    {(formData.resistanceBehavior.resistanceTriggers || []).map((t, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="bold">Trigger #{idx + 1}</Typography>
                          <IconButton size="small" color="error" onClick={() => removeResistanceTrigger(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 5 }}>
                            <TextField
                              fullWidth size="small" label="Trigger" multiline rows={2}
                              value={t.trigger || ''} onChange={(e) => updateResistanceTrigger(idx, 'trigger', e.target.value)}
                              disabled={isLoading}
                              placeholder="What triggers the resistance"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 5 }}>
                            <TextField
                              fullWidth size="small" label="Response" multiline rows={2}
                              value={t.response || ''} onChange={(e) => updateResistanceTrigger(idx, 'response', e.target.value)}
                              disabled={isLoading}
                              placeholder="How the client responds"
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 2 }}>
                            <Typography variant="body2" gutterBottom>Intensity: {t.intensity}</Typography>
                            <Slider
                              value={t.intensity || 5} min={1} max={10} step={1}
                              valueLabelDisplay="auto"
                              onChange={(_, val) => updateResistanceTrigger(idx, 'intensity', val)}
                              disabled={isLoading}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                    <Button
                      startIcon={<AddIcon />} variant="outlined" size="small"
                      onClick={addResistanceTrigger} disabled={isLoading}
                    >
                      Add Trigger
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* ── Section 8: Quick Test ─────────────────────────────────────── */}
        {isEditing && (
          <Accordion
            expanded={expandedAccordion === 'quick-test'}
            onChange={handleAccordionChange('quick-test')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ScienceIcon sx={{ mr: 1, color: 'secondary.main' }} />
              <Typography variant="subtitle1">Quick Realism Test</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Run a 20-turn simulation and evaluate this persona across 6 realism dimensions.
              </Typography>

              {/* Therapist selection */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Therapist Persona</InputLabel>
                <Select
                  value={selectedTherapistKey}
                  onChange={(e) => setSelectedTherapistKey(e.target.value)}
                  label="Therapist Persona"
                  disabled={quickTestPhase === 'simulating' || quickTestPhase === 'reviewing'}
                >
                  {therapistPersonas.map(tp => (
                    <MenuItem key={tp.key || tp._id} value={tp.key || tp._id}>
                      {tp.name || tp.key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Run button */}
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                startIcon={quickTestPhase === 'simulating' || quickTestPhase === 'reviewing'
                  ? <CircularProgress size={18} />
                  : <ScienceIcon />}
                onClick={handleQuickTest}
                disabled={!selectedTherapistKey || quickTestPhase === 'simulating' || quickTestPhase === 'reviewing'}
                sx={{ mb: 2 }}
              >
                {quickTestPhase === 'idle' || quickTestPhase === 'complete' || quickTestPhase === 'error'
                  ? 'Run Quick Test'
                  : quickTestProgress}
              </Button>

              {/* Progress */}
              {(quickTestPhase === 'simulating' || quickTestPhase === 'reviewing') && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">{quickTestProgress}</Typography>
                  <LinearProgress
                    color="secondary"
                    variant={quickTestPhase === 'reviewing' ? 'indeterminate' : 'indeterminate'}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              )}

              {/* Error */}
              {quickTestPhase === 'error' && (
                <Alert severity="error" sx={{ mb: 2 }}>{quickTestError}</Alert>
              )}

              {/* Results */}
              {quickTestResults && quickTestPhase === 'complete' && (
                <Box>
                  {/* Overall Score */}
                  <Card variant="outlined" sx={{ mb: 2, bgcolor: 'grey.50' }}>
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="caption" color="text.secondary">Overall Realism Score</Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 'bold',
                          color: (quickTestResults.weightedQualityScore || 0) >= 70
                            ? 'success.main'
                            : (quickTestResults.weightedQualityScore || 0) >= 40
                              ? 'warning.main'
                              : 'error.main'
                        }}
                      >
                        {quickTestResults.weightedQualityScore?.toFixed(1) || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">/ 100</Typography>
                    </CardContent>
                  </Card>

                  {/* Dimension Breakdown */}
                  {quickTestResults.qualityBreakdown && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Dimension Scores</Typography>
                      {Object.entries(quickTestResults.qualityBreakdown).map(([dimName, dim]) => {
                        const score = dim.score || 0;
                        const maxScore = dim.maxScore || 10;
                        const pct = (score / maxScore) * 100;
                        return (
                          <Accordion key={dimName} variant="outlined" sx={{ mb: 0.5 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                                <Typography variant="body2" sx={{ minWidth: 160, textTransform: 'capitalize' }}>
                                  {dimName.replace(/_/g, ' ')}
                                </Typography>
                                <Box sx={{ flex: 1, mx: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    color={pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'error'}
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                </Box>
                                <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'right', fontWeight: 'bold' }}>
                                  {score}/{maxScore}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              {dim.evidence && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">Evidence:</Typography>
                                  <Typography variant="body2" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, fontStyle: 'italic', fontSize: '0.85rem' }}>
                                    &ldquo;{dim.evidence}&rdquo;
                                  </Typography>
                                </Box>
                              )}
                              {dim.calibrationNote && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Calibration Note:</Typography>
                                  <Typography variant="body2" sx={{ bgcolor: 'warning.lighter', p: 1, borderRadius: 1, fontSize: '0.85rem' }}>
                                    {dim.calibrationNote}
                                  </Typography>
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  )}

                  {/* Calibration Issues */}
                  {quickTestResults.calibrationIssues?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Calibration Issues ({quickTestResults.calibrationIssues.length})
                      </Typography>
                      {quickTestResults.calibrationIssues.map((issue, i) => (
                        <Alert
                          key={i}
                          severity={issue.severity === 'critical' || issue.severity === 'high' ? 'error' : issue.severity === 'medium' ? 'warning' : 'info'}
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{issue.issue}</Typography>
                          {issue.parameterPath && (
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              Parameter: <code>{issue.parameterPath}</code>
                            </Typography>
                          )}
                          {issue.observed && (
                            <Typography variant="caption" sx={{ display: 'block' }}>Observed: {issue.observed}</Typography>
                          )}
                          {issue.expected && (
                            <Typography variant="caption" sx={{ display: 'block' }}>Expected: {issue.expected}</Typography>
                          )}
                        </Alert>
                      ))}
                    </Box>
                  )}

                  {/* Persona Improvement Suggestions */}
                  {quickTestResults.personaImprovements?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Improvement Suggestions ({quickTestResults.personaImprovements.length})
                      </Typography>
                      {quickTestResults.personaImprovements.map((imp, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            {imp.priority && (
                              <Chip
                                label={imp.priority.toUpperCase()}
                                size="small"
                                color={imp.priority === 'high' || imp.priority === 'critical' ? 'error' : imp.priority === 'medium' ? 'warning' : 'info'}
                                variant="outlined"
                              />
                            )}
                            {imp.parameterPath && (
                              <Typography variant="caption" color="text.secondary"><code>{imp.parameterPath}</code></Typography>
                            )}
                          </Box>
                          {imp.suggestedChange && (
                            <Typography variant="body2" sx={{ mb: 0.5 }}>{imp.suggestedChange}</Typography>
                          )}
                          {imp.reasoning && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {imp.reasoning}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  )}

                  {/* Evaluation text */}
                  {quickTestResults.evaluation && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Evaluation</Typography>
                      <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {quickTestResults.evaluation}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {/* Recommendations */}
                  {quickTestResults.recommendations && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Recommendations</Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'warning.lighter' }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {quickTestResults.recommendations}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* ── Save Button ──────────────────────────────────────────────── */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            type="submit" variant="contained" disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            {isEditing ? 'Update Persona' : 'Add Persona'}
          </Button>
        </Box>
      </form>

      {/* ── Import Confirmation Dialog ─────────────────────────────────── */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Persona Import</DialogTitle>
        <DialogContent>
          {importPreview && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Importing <strong>{importPreview.personas.length}</strong> persona{importPreview.personas.length !== 1 ? 's' : ''}:
                {' '}{importPreview.enrichedCount > 0 && `${importPreview.enrichedCount} enriched`}
                {importPreview.enrichedCount > 0 && importPreview.basicCount > 0 && ', '}
                {importPreview.basicCount > 0 && `${importPreview.basicCount} basic`}
              </Typography>
              {importPreview.newKeys.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="success.main" fontWeight="bold">
                    New ({importPreview.newKeys.length}):
                  </Typography>
                  <List dense disablePadding>
                    {importPreview.newKeys.map(k => (
                      <ListItem key={k} sx={{ py: 0 }}>
                        <ListItemText primary={k} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              {importPreview.updateKeys.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="warning.main" fontWeight="bold">
                    Will update existing ({importPreview.updateKeys.length}):
                  </Typography>
                  <List dense disablePadding>
                    {importPreview.updateKeys.map(k => (
                      <ListItem key={k} sx={{ py: 0 }}>
                        <ListItemText primary={k} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleImportConfirm}>Import</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientPersonaEditor;
